<template>
  <div id="notification-center" class="container">
    <section>
      <div class="header">
        <div class="header-info">
          <div class="title">Notification Centre</div>
          <div class="description">
            Hey {{ capitalizedFirstLetter(user_name) }}, welcome back.
          </div>
        </div>
        <div class="header-search-wrapper">
          <b-form-input
            class="header-search"
            v-model="searchQuery"
            type="text"
            @input="searchNotifications"
            placeholder="Search notifications..."
          >
          </b-form-input>
          <font-awesome-icon icon="search" class="search-icon" />
        </div>
      </div>
    </section>
    <div class="filter" v-if="isFilterShown">
      <div class="description">Filter</div>
      <font-awesome-icon icon="bars-filter" class="filter-icon" />
    </div>
    <div
      v-for="(group, groupIndex) in searchedNotifications"
      :key="group.dateKey"
      class="notification-group"
    >
      <div v-if="group.isToday" class="today-container">
        <p class="date-title">Today{{ timeZoneSuffix(groupIndex) }}</p>
        <p class="today-date">{{ group.formattedDate }}</p>
      </div>
      <h3 v-else class="date-title">
        {{ group.formattedDate }}{{ timeZoneSuffix(groupIndex) }}
      </h3>

      <NotificationItem
        v-for="item in group.items"
        :key="item.id"
        :item="item"
      />
    </div>

    <!-- </div> -->
  </div>
</template>

<script>
import NotificationItem from "@/components/Home/New/NotificationItem.vue";
import {
  getDisplayTimeZone,
  nowInDisplayZone,
} from "@/utils/dateTime/dateTime";
import { isTimezoneSupportEnabled } from "@/utils/timezone/timezone";
import { getViewerTimeZoneLabel } from "@journeys/utils/helpers";

export default {
  name: "NotificationCentre",
  components: { NotificationItem },
  data() {
    return {
      isFilterShown: false,
      searchQuery: "",
      isExpanded: false,
      user_name: this.$store.getters.user.user.first_name,
      areNotificationsLoading: false,
      notifications: [],
      searchedNotifications: [],
      loading: true,
      formattedDate: this.getFormattedDate(),
      groupedNotifications: []
    };
  },
  mounted() {
    this.getNotifications();
  },
  methods: {
    // Australia-only, matching the clinician time picker title; the most recent group only.
    timeZoneSuffix(groupIndex) {
      if (groupIndex !== 0 || !isTimezoneSupportEnabled()) return "";
      const timeZoneLabel = getViewerTimeZoneLabel(
        getDisplayTimeZone() || undefined
      );
      return timeZoneLabel ? ` (${timeZoneLabel})` : "";
    },
    groupeNotifications() {
      const groups = {};
      this.notifications.forEach((notification) => {
        // createdAt is a UTC instant, so which day it falls on depends on the
        // zone — group by the member's, not the device's.
        const dateKey = new Date(notification.createdAt).toLocaleDateString(
          "en-US",
          { timeZone: getDisplayTimeZone() || undefined },
        );
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(notification);
      });
      const todayKey = new Date().toLocaleDateString("en-US", {
        timeZone: getDisplayTimeZone() || undefined,
      });

      this.groupedNotifications = Object.entries(groups)
        .sort((a, b) => new Date(b[0]) - new Date(a[0]))
        .map(([dateKey, items], index) => ({
          dateKey,
          formattedDate: this.formatDateToLongForm(dateKey),
          items,
          isToday: dateKey === todayKey && index === 0
        }));
    },
    getFormattedDate() {
      const date = nowInDisplayZone();

      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];

      const dayName = days[date.day()];
      const day = date.date();
      const month = months[date.month()];
      const year = date.year();

      const suffix = d => {
        if (d > 3 && d < 21) return "th";
        switch (d % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };

      return `${dayName}, ${day}${suffix(day)} of ${month}, ${year}`;
    },
    async getNotifications() {
      try {
        if (this.areNotificationsLoading) return;
        this.areNotificationsLoading = true;
        const res = await this.api({
          path: "api/u/notifications"
        });
        this.notifications = res.data;
        this.groupeNotifications();
        this.searchedNotifications = this.groupedNotifications;
      } catch (err) {
        console.error(err);
      } finally {
        this.areNotificationsLoading = false;
      }
    },
    capitalizedFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    searchNotifications(query) {
      if (!query) {
        this.searchedNotifications = this.groupedNotifications;
        return;
      }

      this.searchedNotifications = this.groupedNotifications
        .map(day => {
          const matchingItems = day.items.filter(item =>
            item.subject.toLowerCase().includes(query.toLowerCase())
          );
          if (matchingItems.length > 0) {
            return {
              ...day,
              items: matchingItems
            };
          }
          return null;
        })
        .filter(day => day !== null);
    },
    formatDateInfo(dateString) {
      const inputDate = new Date(dateString);
      // dateString is already a bare day in the member's zone, so "today" has to
      // be read in that zone too.
      const today = nowInDisplayZone();

      const isToday =
        inputDate.getFullYear() === today.year() &&
        inputDate.getMonth() === today.month() &&
        inputDate.getDate() === today.date();

      const daysOfWeek = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];

      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];

      const day = inputDate.getDate();
      const month = months[inputDate.getMonth()];
      const year = inputDate.getFullYear();

      const getOrdinal = n => {
        if (n > 3 && n < 21) return "th";
        switch (n % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };

      const dayLabel = isToday ? "Today" : daysOfWeek[inputDate.getDay()];
      const formattedDate = `${day}${getOrdinal(day)} of ${month}, ${year}`;

      return {
        today: isToday,
        day: dayLabel,
        date: formattedDate
      };
    },
    getCurrentTimeFormatted() {
      const now = nowInDisplayZone();
      let hours = now.hour();
      const minutes = now.minute();
      const ampm = hours >= 12 ? "pm" : "am";

      hours = hours % 12;
      hours = hours ? hours : 12;

      const paddedMinutes = minutes < 10 ? `0${minutes}` : minutes;

      return `${hours}:${paddedMinutes}${ampm}`;
    }
  }
};
</script>

<style scoped lang="less">
#notification-center {
  font-family: "NunitoSans", serif;

  &.container {
    padding: 20px;
  }

  .title {
    font-size: 4rem;
    font-family: "Raptor V3 Black";
    color: #1f1e1e;
  }

  .description {
    font-family: "DMSans";
    font-size: 2.4rem;
    font-weight: 500;
    color: #1f1e1e;
  }

  .date-container {
    display: flex;
    justify-content: space-between;
    padding-bottom: 6px;
  }

  .notification-day {
    padding-bottom: 35px;
  }

  .notification-time {
    font-size: 20px;
    font-weight: 500;
    display: flex;
    justify-content: flex-end;
  }

  .date-bold {
    font-size: 20px;
    font-weight: 900;
  }

  .date {
    font-size: 20px;
    font-weight: 500;
  }

  .today-container {
    display: flex;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
  }

  .date-title {
    color: #444444;
    font-family: "Raptor V3 Black" !important;
    font-size: 2.4rem;
    letter-spacing: normal;
    padding: 0;
  }

  .today-date {
    color: #444444;
    font-family: "DMSans";
    font-size: 2rem;
  }

  .header {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;

    .header-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .header-search-wrapper {
      position: relative;
      width: 474px;

      .header-search {
        width: 100%;
        height: 57px;
        border-radius: 9px;
        border: 1px solid var(--brand-primary);
        background: #ffffff;
        padding-inline: 20px;
        padding-right: 50px; // Make room for the icon
        font-size: 20px;
        font-weight: 500;
        color: #122130;
      }

      .search-icon {
        width: 20px;
        height: 20px;
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--brand-primary);
        pointer-events: none;
      }
    }
  }

  .filter {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;

    .filter-icon {
      font-size: 24px;
      font-weight: bold;
      color: var(--brand-primary);
      cursor: pointer;
      margin-bottom: 0;
    }
  }

  .notification {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .card {
    background: #fff;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .card-title {
    display: flex;
    gap: 0.75rem;
  }

  .icon-calendar,
  .icon-message {
    width: 24px;
    height: 24px;
    background-color: #dce4ff;
    border-radius: 4px;
  }

  .text-content .title {
    font-weight: bold;
    margin: 0;
  }

  .text-content .subtitle {
    font-size: 0.875rem;
    color: #555;
    margin: 0;
  }

  .card-time-action {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .time {
    font-size: 0.75rem;
    color: #999;
  }

  .view-btn {
    background-color: #0a53be;
    color: white;
    border: none;
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
    border-radius: 4px;
    cursor: pointer;
  }

  /* Expanded content styles */

  .expanded-content .card-body {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #333;
  }

  .expanded-content ul {
    padding-left: 1rem;
    margin: 0.5rem 0;
  }

  .expanded-content ul li {
    margin-bottom: 0.4rem;
  }
}
</style>
